import { delay } from 'redux-saga';
import { call, put, takeLatest, takeEvery, select, take, fork } from 'redux-saga/effects';

import { isProductSupportMode, isReadonlyMode } from 'system/SystemUtil';

import { gcApi } from 'config/axios';

import {
    fetchMultipleUserSocialProfileImageStart,
    fetchMultipleUserSocialProfileImageDone,
    fetchUserSocialProfileImageViewerDone,
    fetchMultipleUserSocialProfileImage,
    fetchUserSocialProfileImageViewer,
    removeViewerSocialProfileImageDone
} from 'actions/userSocialProfileImageActions';
import {
    FETCH_MULTIPLE_USER_SOCIAL_PROFILE_IMAGE,
    FETCH_MULTIPLE_USER_SOCIAL_PROFILE_IMAGE_DONE,
    FETCH_USER_SOCIAL_PROFILE_IMAGE_VIEWER,
    RECEIVE_SYSTEM_PREFERENCE,
    REMOVE_VIEWER_SOCIAL_PROFILE_IMAGE,
    UPDATE_VIEWER_PUBLIC_PROFILE_GC_PLUS
} from 'actions/ActionTypes';

import { fetchSystemPreference } from 'actions';
import ViewerSocialProfileModel from 'models/user/ViewerSocialProfile';
import { getSystemPreferenceValue } from 'reducers/system/systemPreferences';
import {
    gcPlusFrameRoleKey // Clean up ticket: CA-165763
} from 'models/preferences/PreferencesMapping';
import {
    selectProfileImageDtoFetchedTsState,
    selectProfileImageDtoFetchingList,
    selectProfileImageDtoOfUserId,
    selectUserSocialProfileImageViewer
} from 'reducers/userSocialProfileImage/userSocialProfileImageReducer';

// utils
import { toQueryString } from 'utils/UrlUtil';

// utils -- userSocialProfileImage
import profileImageHelperV2 from 'utils/userSocialProfileImage/profileImageHelperV2';
import { PROFILE_IMAGE_VERSION } from 'utils/userSocialProfileImage/profileImageConstantsV2';
import 'utils/userSocialProfileImage/profileImageTypeDefsV2';

export function* initUserSocialProfileImageSaga() {
    /** trigger feature flag fetching on initialize */
    yield put(fetchSystemPreference(gcPlusFrameRoleKey)); // Clean up ticket: CA-165763

    /** awaits gcPlusFrameRoleKey and init viewer profile image and frame fetching */
    while (true) {
        const action = yield take(RECEIVE_SYSTEM_PREFERENCE);
        if (!action) {
            continue;
        }
        // Clean up ticket: CA-165763
        if (action.key === gcPlusFrameRoleKey) {
            break;
        }
    }

    const gcPlusFrameRoleValue = yield select(getSystemPreferenceValue, gcPlusFrameRoleKey); // Clean up ticket: CA-165763

    const featureOn =
        gcPlusFrameRoleValue === 'ROLE_CONNECTUSER' || ViewerSocialProfileModel.hasRole(gcPlusFrameRoleValue);

    if (!featureOn) {
        profileImageHelperV2.setDefaultImageVersion(1);
        return;
    }

    profileImageHelperV2.setDefaultImageVersion(2);

    /** softer remove all label-pro (by hiding them) */
    document.body.classList.add('no-label-pro');

    yield fork(initViewerSocialProfileImageAndFrameSaga);
}

export function* hasUserProfileFrameFeature() {
    const gcPlusFrameRoleValue = yield select(getSystemPreferenceValue, gcPlusFrameRoleKey); // Clean up ticket: CA-165763

    const featureOn =
        gcPlusFrameRoleValue === 'ROLE_CONNECTUSER' || ViewerSocialProfileModel.hasRole(gcPlusFrameRoleValue);

    return featureOn;
}

/**
 * similar to `hasUserProfileFrameFeature`, but wait til feature flag is fetched
 * @returns
 */
export function* forceCheckHasUserProfileFrameFeature() {
    /** awaits gcPlusFrameRoleKey and init viewer profile image and frame fetching */
    let gcPlusFrameRoleValue = yield select(getSystemPreferenceValue, gcPlusFrameRoleKey); // Clean up ticket: CA-165763
    while (gcPlusFrameRoleValue === null) {
        const action = yield take(RECEIVE_SYSTEM_PREFERENCE);
        if (!action) {
            continue;
        }
        gcPlusFrameRoleValue = yield select(getSystemPreferenceValue, gcPlusFrameRoleKey); // Clean up ticket: CA-165763
        // Clean up ticket: CA-165763
        if (action.key === gcPlusFrameRoleKey) {
            break;
        }
    }
    const featureOn =
        gcPlusFrameRoleValue === 'ROLE_CONNECTUSER' || ViewerSocialProfileModel.hasRole(gcPlusFrameRoleValue);
    profileImageHelperV2.setDefaultImageVersion(featureOn ? 2 : 1);
    return featureOn;
}

function* initViewerSocialProfileImageAndFrameSaga() {
    if (isReadonlyMode() || isProductSupportMode()) {
        return;
    }
    yield delay(1000); // delay 1000ms then trigger fetching (sometimes top header isn't rendered yet)
    const featureOn = yield call(hasUserProfileFrameFeature);

    if (featureOn) {
        const profileId = ViewerSocialProfileModel.get('profileId');
        yield put(fetchMultipleUserSocialProfileImage([profileId])); // fetch self
    }
}

function* fetchUserSocialProfileImageSaga() {
    try {
        /**
         * swagger
         * https://connectapitest-swagger.garmin.com/?urls.primaryName=userprofile-service#/default/getSocialProfileImageById
         */
        const url = `/userprofile-service/socialProfile/image`;
        const response = yield call(gcApi.get, url);
        yield put(fetchUserSocialProfileImageViewerDone(response.data));
    } catch (error) {
        yield put(fetchUserSocialProfileImageViewerDone(null, true));
    }
}

const CACHED_LIMIT_TS = 1000 * 60; // cache image response for 1 min

function* fetchMultipleUserSocialProfileImageSaga(action) {
    const featureOn = yield call(hasUserProfileFrameFeature);

    const fetchingList = yield select(selectProfileImageDtoFetchingList);
    const fetchedTs = yield select(selectProfileImageDtoFetchedTsState);

    const now = Date.now();

    if (!featureOn) {
        /** feature off, exit */
        yield put(fetchMultipleUserSocialProfileImageDone({ data: null }));
        return;
    }

    let userIds = action.userIds;

    userIds = userIds.filter((userId) => {
        if (!userId) {
            return false;
        }
        if (fetchingList.includes(userId)) {
            return false;
        }
        if (action.clearCache) {
            return true;
        }
        return (fetchedTs[userId] || 0) + CACHED_LIMIT_TS < now;
    });

    /** remove duplicate */
    userIds = Object.values(
        userIds.reduce((acc, item) => {
            acc[item] = item;
            return acc;
        }, {})
    );

    const imageVersions = Object.values(PROFILE_IMAGE_VERSION);

    const querystring = toQueryString({ userId: userIds, 'image-version': imageVersions });

    if (!userIds.length) {
        yield put(fetchMultipleUserSocialProfileImageDone({ data: null }));
        return;
    }

    /** update reducer */
    yield put(fetchMultipleUserSocialProfileImageStart(userIds));

    try {
        /**
         * swagger
         * https://connectapitest-swagger.garmin.com/?urls.primaryName=userprofile-service#/Social%20Profile%20V2/getMultiplePublicSocialProfileByUserId
         */
        const response = yield call(
            gcApi.get,
            `/userprofile-service/socialProfile/v2/public/multiple/id${querystring}`
        );
        yield put(fetchMultipleUserSocialProfileImageDone({ data: response.data }));
    } catch (error) {
        yield put(fetchMultipleUserSocialProfileImageDone({ error: true, data: null }));
    }
}

function* updateViewerTopHeaderProfileImage() {
    if (isReadonlyMode() || isProductSupportMode()) {
        return;
    }
    const profileId = ViewerSocialProfileModel.get('profileId');

    /**
     * @type {SimplePublicSocialProfileV2DTO}
     */
    const profileImageDtoOfViewer = yield select(selectProfileImageDtoOfUserId, profileId);

    if (!profileImageDtoOfViewer) {
        return;
    }

    const imgSrc = profileImageHelperV2.getViewerMediumIconImage(
        profileImageDtoOfViewer,
        ViewerSocialProfileModel.toJSON()
    );
    /**
     * @type {NodeListOf<HTMLImageElement>}
     */
    const headerImageElement = document.querySelectorAll('.header-nav-item img.avatar');
    if (!!headerImageElement[0]) {
        headerImageElement[0].src = imgSrc;
        if (profileImageHelperV2.isNoclipProfile(imgSrc)) {
            headerImageElement[0].onload = () => {
                headerImageElement[0].dataset.noclip = 'true';
            };
        }
        headerImageElement[0].onerror = () => {
            headerImageElement[0].src = profileImageHelperV2.getDefaultProfileImage();
            delete headerImageElement[0].dataset.noclip;
        };
    }
}

const GC_PROFILE_IMAGE_TYPE = {
    UPLOADED_PHOTO: 'UPLOADED_PHOTO',
    CONNECT_AVATAR: 'CONNECT_AVATAR',
    THEMED_AVATAR: 'THEMED_AVATAR',
    DEFAULT_AVATAR: 'DEFAULT_AVATAR'
};

/**
 * @enum {string}
 */
const UPLOAD_PROFILE_RES_IMAGE_TYPE = {
    Profile: 'Profile',
    Friends: 'Friends',
    Thumbnail: 'Thumbnail',
    Framed_Profile: atob('RnJhbWVkX1Byb2ZpbGU='),
    Framed_Friends: atob('RnJhbWVkX0ZyaWVuZHM='),
    Framed_Thumbnail: atob('RnJhbWVkX1RodW1ibmFpbA=='),
    Premium_Profile: atob('UHJlbWl1bV9Qcm9maWxl'),
    Premium_Friends: atob('UHJlbWl1bV9GcmllbmRz'),
    Premium_Thumbnail: atob('UHJlbWl1bV9UaHVtYm5haWw=')
};

const LARGE_IMAGE_TYPES = [
    UPLOAD_PROFILE_RES_IMAGE_TYPE.Profile,
    UPLOAD_PROFILE_RES_IMAGE_TYPE.Framed_Profile,
    UPLOAD_PROFILE_RES_IMAGE_TYPE.Premium_Profile
];

const MEDIUM_IMAGE_TYPES = [
    UPLOAD_PROFILE_RES_IMAGE_TYPE.Friends,
    UPLOAD_PROFILE_RES_IMAGE_TYPE.Framed_Friends,
    UPLOAD_PROFILE_RES_IMAGE_TYPE.Premium_Friends
];

const SMALL_IMAGE_TYPES = [
    UPLOAD_PROFILE_RES_IMAGE_TYPE.Thumbnail,
    UPLOAD_PROFILE_RES_IMAGE_TYPE.Framed_Thumbnail,
    UPLOAD_PROFILE_RES_IMAGE_TYPE.Premium_Thumbnail
];

/**
 *
 * @param {UPLOAD_PROFILE_RES_IMAGE_TYPE} resImageType
 * @param {UPLOAD_PROFILE_RES_IMAGE_TYPE} newResImageType
 * @returns
 */
function shouldReplaceCallback(resImageType, newResImageType) {
    if (
        !resImageType ||
        resImageType === UPLOAD_PROFILE_RES_IMAGE_TYPE.Profile ||
        resImageType === UPLOAD_PROFILE_RES_IMAGE_TYPE.Friends ||
        resImageType === UPLOAD_PROFILE_RES_IMAGE_TYPE.Thumbnail
    ) {
        return true;
    }
    if (resImageType === UPLOAD_PROFILE_RES_IMAGE_TYPE.Framed_Profile) {
        return newResImageType === UPLOAD_PROFILE_RES_IMAGE_TYPE.Premium_Profile;
    }
    if (resImageType === UPLOAD_PROFILE_RES_IMAGE_TYPE.Framed_Friends) {
        return newResImageType === UPLOAD_PROFILE_RES_IMAGE_TYPE.Premium_Friends;
    }
    if (resImageType === UPLOAD_PROFILE_RES_IMAGE_TYPE.Framed_Thumbnail) {
        return newResImageType === UPLOAD_PROFILE_RES_IMAGE_TYPE.Premium_Thumbnail;
    }
    return false;
}

/**
 *
 * @param {UPLOAD_PROFILE_RES_IMAGE_TYPE} resImageType
 * @returns
 */
function resImageTypeToImageVersion(resImageType) {
    switch (resImageType) {
        case UPLOAD_PROFILE_RES_IMAGE_TYPE.Profile:
            return PROFILE_IMAGE_VERSION.PROFILE;
        case UPLOAD_PROFILE_RES_IMAGE_TYPE.Friends:
            return PROFILE_IMAGE_VERSION.PROFILE_FRIEND;
        case UPLOAD_PROFILE_RES_IMAGE_TYPE.Thumbnail:
            return PROFILE_IMAGE_VERSION.PROFILE_THUMBNAIL;
        case UPLOAD_PROFILE_RES_IMAGE_TYPE.Framed_Profile:
            return PROFILE_IMAGE_VERSION.FRAMED_PROFILE;
        case UPLOAD_PROFILE_RES_IMAGE_TYPE.Framed_Friends:
            return PROFILE_IMAGE_VERSION.FRAMED_FRIEND;
        case UPLOAD_PROFILE_RES_IMAGE_TYPE.Framed_Thumbnail:
            return PROFILE_IMAGE_VERSION.FRAMED_THUMBNAIL;
        case UPLOAD_PROFILE_RES_IMAGE_TYPE.Premium_Profile:
            return PROFILE_IMAGE_VERSION.PREMIUM_PROFILE;
        case UPLOAD_PROFILE_RES_IMAGE_TYPE.Premium_Friends:
            return PROFILE_IMAGE_VERSION.PREMIUM_FRIEND;
        case UPLOAD_PROFILE_RES_IMAGE_TYPE.Premium_Thumbnail:
            return PROFILE_IMAGE_VERSION.PREMIUM_THUMBNAIL;
        default:
            return '';
    }
}

export function* updateUserPublicProfileGcPlusSaga(action) {
    if (!action.payload) {
        return;
    }

    const { displayName, resizedImages, avatarUuid, frameUuid, hasSubscribed, useDefaultProfile } = action.payload;

    const currentSocialProfileImageDTO = yield select(selectUserSocialProfileImageViewer);

    const callback = typeof action.payload.callback === 'function' ? action.payload.callback : () => {};

    const url = `/image-service/imageservice/upload/${displayName}`;

    let formData = new FormData();

    const headers = {};

    if (resizedImages) {
        /** new images */
        if (!resizedImages.large || !resizedImages.medium || !resizedImages.small) {
            callback(new Error('require image blobs'));
            return;
        }
        headers['x-gc-profile-image-type'] = GC_PROFILE_IMAGE_TYPE.UPLOADED_PHOTO;

        formData.append('profileFile', resizedImages.large, 'fileName.png');
        formData.append('friendsFile', resizedImages.medium, 'fileName2.png');
        formData.append('thumbnailFile', resizedImages.small, 'fileName3.png');
    } else {
        if (avatarUuid) {
            headers['x-gc-profile-image-type'] = GC_PROFILE_IMAGE_TYPE.CONNECT_AVATAR;
            headers['x-gc-profile-image-avatar-key'] = avatarUuid;
        } else if (useDefaultProfile) {
            headers['x-gc-profile-image-type'] = GC_PROFILE_IMAGE_TYPE.DEFAULT_AVATAR;
        } else {
            headers['x-gc-profile-image-type'] = currentSocialProfileImageDTO?.profileImageType;
        }
    }

    if (frameUuid && hasSubscribed) {
        headers['x-gc-profile-image-frame-key'] = frameUuid;
    } else {
        headers['x-gc-profile-image-frame-key'] = 'NO_FRAME';
    }
    try {
        const response = yield call(gcApi.post, url, formData, { headers });
        yield put(fetchUserSocialProfileImageViewer(true));
        if (
            headers['x-gc-profile-image-type'] === GC_PROFILE_IMAGE_TYPE.UPLOADED_PHOTO ||
            headers['x-gc-profile-image-type'] === GC_PROFILE_IMAGE_TYPE.CONNECT_AVATAR ||
            headers['x-gc-profile-image-type'] === GC_PROFILE_IMAGE_TYPE.DEFAULT_AVATAR
        ) {
            /**
             * @type {SimplePublicSocialProfileV2DTO}
             */
            const dto = {
                profileId: ViewerSocialProfileModel.get('profileId'),
                displayName: ViewerSocialProfileModel.get('displayName'),
                garminGuid: ViewerSocialProfileModel.get('garminGuid'),
                fullName: ViewerSocialProfileModel.get('fullName'),
                profileImageUrls: {}
            };
            /**
             *
             * @type {{ imageType: string; url: string; }[]}
             */
            const profileImages = response.data || [];

            if (profileImages.length > 0) {
                const callbackData = {};
                profileImages.forEach((item) => {
                    if (
                        LARGE_IMAGE_TYPES.includes(item.imageType) &&
                        shouldReplaceCallback(callbackData.largeImageType, item.imageType)
                    ) {
                        callbackData.profileImageUrlLarge = item.url;
                        callbackData.largeImageType = item.imageType;
                    }

                    if (
                        MEDIUM_IMAGE_TYPES.includes(item.imageType) &&
                        shouldReplaceCallback(callbackData.mediumImageType, item.imageType)
                    ) {
                        callbackData.profileImageUrlMedium = item.url;
                        callbackData.mediumImageType = item.imageType;
                    }

                    if (
                        SMALL_IMAGE_TYPES.includes(item.imageType) &&
                        shouldReplaceCallback(callbackData.smallImageType, item.imageType)
                    ) {
                        callbackData.profileImageUrlSmall = item.url;
                        callbackData.smallImageType = item.imageType;
                    }

                    const imageVersion = resImageTypeToImageVersion(item.imageType);
                    if (imageVersion) {
                        dto.profileImageUrls[imageVersion] = item.url;
                    }
                });
                callback(null, callbackData);

                yield put(fetchMultipleUserSocialProfileImageDone({ data: { [dto.profileId]: dto } }));
            } else {
                callback(null);
                const profileId = ViewerSocialProfileModel.get('profileId');
                yield put(fetchMultipleUserSocialProfileImage([profileId], true));
            }

            if (useDefaultProfile) {
                yield put(removeViewerSocialProfileImageDone());
            }
        }
    } catch (error) {
        callback(error);
    }
}

export function* removeUserSocialProfileImageSaga(action) {
    const { displayName, hasSubscribed, callback = () => {} } = action;
    if (!hasSubscribed) {
        try {
            yield call(gcApi.delete, `/image-service/imageservice/remove/${displayName}`);
            ViewerSocialProfileModel.set({
                profileImageUuid: null,
                profileImageUrlLarge: null,
                profileImageUrlMedium: null,
                profileImageUrlSmall: null
            });
            /**
             * @type {SimplePublicSocialProfileV2DTO}
             */
            const dto = {
                profileId: ViewerSocialProfileModel.get('profileId'),
                displayName: ViewerSocialProfileModel.get('displayName'),
                garminGuid: ViewerSocialProfileModel.get('garminGuid'),
                fullName: ViewerSocialProfileModel.get('fullName'),
                profileImageUrls: {}
            };
            yield put(fetchMultipleUserSocialProfileImageDone({ data: { [dto.profileId]: dto } }));
            callback(null);
        } catch (error) {
            callback(error);
        }
        yield put(removeViewerSocialProfileImageDone());
        return;
    }
    const currentSocialProfileImageDTO = yield select(selectUserSocialProfileImageViewer);
    const frameUuid = currentSocialProfileImageDTO?.profileFrameKey;
    yield fork(updateUserPublicProfileGcPlusSaga, {
        payload: { useDefaultProfile: true, displayName, frameUuid, hasSubscribed, callback }
    });
}

export const userSocialProfileImageSagas = [
    initUserSocialProfileImageSaga(),
    takeLatest(FETCH_USER_SOCIAL_PROFILE_IMAGE_VIEWER, fetchUserSocialProfileImageSaga),
    takeEvery(FETCH_MULTIPLE_USER_SOCIAL_PROFILE_IMAGE, fetchMultipleUserSocialProfileImageSaga),
    takeLatest(FETCH_MULTIPLE_USER_SOCIAL_PROFILE_IMAGE_DONE, updateViewerTopHeaderProfileImage),
    takeLatest(UPDATE_VIEWER_PUBLIC_PROFILE_GC_PLUS, updateUserPublicProfileGcPlusSaga),
    takeLatest(REMOVE_VIEWER_SOCIAL_PROFILE_IMAGE, removeUserSocialProfileImageSaga)
];
